import { useEffect, useState, type ReactNode } from 'react';
import { ActionIcon, Popover, TextInput } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { formatDay, isIsoDay } from '@parts/shared';
import { t } from '../i18n';
import { IconCalendar } from '../icons';

interface Props {
  label: ReactNode;
  description?: string;
  placeholder?: string;
  withAsterisk?: boolean;
  /** Дата в виде «ГГГГ-ММ-ДД» или пусто. */
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
}

/** «ДД.ММ.ГГГГ» → «ГГГГ-ММ-ДД». Понимает 1-2 цифры в дне и месяце и 2 цифры в годе. */
function parseDay(text: string): string | null {
  const m = text.trim().match(/^(\d{1,2})[.,/\-\s](\d{1,2})[.,/\-\s](\d{2}|\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m as unknown as [string, string, string, string];
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  const iso = `${year}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  return isIsoDay(iso) ? iso : null;
}

/**
 * Поле даты: дата набирается как 30.11.2026 или выбирается в календаре.
 * Текст разбирается по выходу из поля, поэтому набор не мешает вводу.
 */
export function DayInput({ label, description, placeholder, withAsterisk, value, onChange, className, inputClassName }: Props) {
  const [text, setText] = useState(value ? formatDay(value) : '');
  const [focused, setFocused] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!focused) setText(value ? formatDay(value) : '');
  }, [value, focused]);

  const commit = () => {
    setFocused(false);
    const trimmed = text.trim();
    if (!trimmed) {
      onChange('');
      return;
    }
    const iso = parseDay(trimmed);
    if (iso) {
      onChange(iso);
      setText(formatDay(iso));
    } else {
      setText(value ? formatDay(value) : '');
    }
  };

  const invalid = focused && text.trim() !== '' && parseDay(text) === null;

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <TextInput
          label={label}
          description={description}
          withAsterisk={withAsterisk}
          placeholder={placeholder ?? t('дд.мм.гггг')}
          value={text}
          error={invalid ? t('Дата в формате дд.мм.гггг') : undefined}
          onFocus={() => setFocused(true)}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
          }}
          className={className}
          classNames={inputClassName ? { input: inputClassName } : undefined}
          rightSection={
            <ActionIcon variant="subtle" color="gray" onClick={() => setOpened((o) => !o)} aria-label={t('Календарь')}>
              <IconCalendar size={15} />
            </ActionIcon>
          }
        />
      </Popover.Target>
      <Popover.Dropdown p={6}>
        <DatePicker
          value={value || null}
          onChange={(v) => {
            onChange(v ?? '');
            setText(v ? formatDay(v) : '');
            setOpened(false);
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
